import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import invoiceService from "../services/invoiceService";
import { apiService } from "../services/api";

const DepositSuccess: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const depositId = searchParams.get("depositId");
  const sessionId = searchParams.get("session_id");

  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [invoiceNumber, setInvoiceNumber] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchInvoice = async () => {
      // Checkout flow: resolve depositId from session, retrying until webhook fires
      if (sessionId && !depositId) {
        let resolvedDepositId: string | null = null;
        for (let attempt = 0; attempt < 6; attempt++) {
          try {
            const response: any = await apiService.get(
              `/deposits/by-checkout/${sessionId}`,
            );
            const deposit = response?.data?.data || response?.data || response;
            if (deposit?._id || deposit?.id) {
              resolvedDepositId = deposit._id || deposit.id;
              break;
            }
          } catch {
            // not ready yet
          }
          await new Promise((res) => setTimeout(res, 2500));
        }

        if (!resolvedDepositId) {
          setIsLoadingInvoice(false);
          return;
        }

        try {
          const invoice =
            await invoiceService.getInvoiceByDepositId(resolvedDepositId);
          const invoiceIdValue = (invoice as any)._id || (invoice as any).id;
          setInvoiceId(invoiceIdValue);
          setInvoiceNumber(invoice.invoiceNumber);
          if (invoiceIdValue) {
            try {
              const url = await invoiceService.getInvoicePdfUrl(invoiceIdValue);
              setPdfUrl(url);
            } catch {}
          }
        } catch {}
        setIsLoadingInvoice(false);
        return;
      }

      // Legacy flow: use depositId from URL
      if (!depositId) {
        setIsLoadingInvoice(false);
        return;
      }

      try {
        const invoice = await invoiceService.getInvoiceByDepositId(depositId);

        // Mongoose sometimes serializes _id as id
        const invoiceIdValue = (invoice as any)._id || (invoice as any).id;

        setInvoiceId(invoiceIdValue);
        setInvoiceNumber(invoice.invoiceNumber);

        // Fetch PDF URL for preview
        if (invoiceIdValue) {
          try {
            const url = await invoiceService.getInvoicePdfUrl(invoiceIdValue);
            setPdfUrl(url);
          } catch (pdfError) {
            console.error("Failed to retrieve invoice PDF URL.");
          }
        }
      } catch (error) {
        console.error("Failed to fetch invoice:");
        // Don't show error toast - invoice might not be generated yet
      } finally {
        setIsLoadingInvoice(false);
      }
    };

    fetchInvoice();
  }, [depositId, sessionId]);

  const handleContinueToHistory = () => {
    navigate("/history");
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-4xl w-full bg-white rounded-xl shadow-lg p-8">
        {/* Invoice Section */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-secondary mb-4">
            {t("deposit.invoiceInformation")}
          </h2>

          {isLoadingInvoice ? (
            <div className="flex items-center justify-center py-16 bg-gray-50 rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-sm text-gray-600">
                {t("deposit.generatingInvoice")}
              </span>
            </div>
          ) : invoiceId && pdfUrl ? (
            <div className="space-y-4">
              {/* Invoice Number */}
              <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">
                  {t("deposit.invoiceNumber")}
                </span>
                <span className="font-mono text-sm font-medium text-secondary">
                  {invoiceNumber || "N/A"}
                </span>
              </div>

              {/* PDF Preview */}
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <iframe
                  src={pdfUrl}
                  className="w-full h-[600px]"
                  title="Invoice PDF Preview"
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">
                {t("deposit.invoiceWillBeAvailableSoon")}
              </p>
            </div>
          )}
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinueToHistory}
          className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-secondary hover:bg-secondary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default DepositSuccess;
